import * as React from 'react';

interface Options {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  /** Minimum horizontal travel (px) before a gesture counts. */
  threshold?: number;
  /** Required |dx| / |dy| ratio, so vertical scrolling is never hijacked. */
  ratio?: number;
  enabled?: boolean;
}

export interface SwipeHandlers {
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerCancel: () => void;
}

/**
 * Pointer-based horizontal swipe detection for the compact Writer shell.
 *
 * Deliberately conservative: only a clearly horizontal, sufficiently long
 * gesture from a touch/pen pointer fires, so vertical chat scrolling,
 * horizontally scrollable code blocks and PDF pinch-zoom keep working.
 */
export function useHorizontalSwipe({
  onSwipeLeft,
  onSwipeRight,
  threshold = 60,
  ratio = 1.5,
  enabled = true,
}: Options): SwipeHandlers {
  const start = React.useRef<{ x: number; y: number; t: number } | null>(null);

  const onPointerDown = React.useCallback(
    (e: React.PointerEvent) => {
      if (!enabled || e.pointerType === 'mouse') return;
      start.current = { x: e.clientX, y: e.clientY, t: Date.now() };
    },
    [enabled],
  );

  const onPointerUp = React.useCallback(
    (e: React.PointerEvent) => {
      const s = start.current;
      start.current = null;
      if (!enabled || !s) return;
      const dx = e.clientX - s.x;
      const dy = e.clientY - s.y;
      if (Date.now() - s.t > 800) return;
      if (Math.abs(dx) < threshold) return;
      if (Math.abs(dx) < Math.abs(dy) * ratio) return;
      if (dx < 0) onSwipeLeft?.();
      else onSwipeRight?.();
    },
    [enabled, threshold, ratio, onSwipeLeft, onSwipeRight],
  );

  const onPointerCancel = React.useCallback(() => {
    start.current = null;
  }, []);

  return { onPointerDown, onPointerUp, onPointerCancel };
}
